import { useEffect, useRef, useState } from "react";
import moment, { Moment } from "moment";
import momentBusinessDays from "moment-business-days";
import {
  Input,
  Card,
  Select,
  Tooltip,
  Tabs,
  message,
  Button,
  Modal,
  DatePicker,
  Col,
  Form,
  Upload,
  Popconfirm,
} from "antd";
import {
  CalendarOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  FlagOutlined,
  LinkOutlined,
  LoadingOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { api } from "../../services/api";
import {
  ContentHeader,
  Kanban,
  Phase,
  TasksContainer,
  Task,
  Loading,
  ModalTaskBody,
  DisplayInRow,
  HorizontalSpace,
  DisplayInColumn,
  VerticalSpace
} from './styles';
import { useLogged } from '../../hooks/userLogin';
import { useRoles } from '../../hooks/useRoles';

import UserAvatar from "../UserAvatar";
import { UploadFile } from "antd/lib/upload/interface";
import { MentionsInput, Mention } from 'react-mentions';
import { useNavigate } from "react-router-dom";

const statuses = [
  { id: 0, title: 'Não iniciada' },
  { id: 1, title: 'Em Progresso' },
  { id: 2, title: 'Finalizada' }
];

interface IUser {
  id: string;
  name: string;
  email: string;
  photo: string;
  role_id: string;
  company_id: any;
}

interface ITaskComment {
  id: string;
  comment: string;
  task_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  user: IUser;
}

interface ITaskHistory {
  id: string;
  key: string;
  old_value: string;
  new_value: string;
  motive?: string;

  user: IUser;

  created_at: string;
  updated_at: string;
}

interface ITaskAttachment {
  id: string;
  filename: string;
  url: string;

  user: IUser;

  created_at: string;
  updated_at: string;
}

interface ITask {
  id: string;
  title: string;
  description: string;
  days: string;
  duration: any;
  phase_id: any;
  parent_id: any;
  plan_init_date: string;
  plan_finish_date: string;
  result_init_date: string;
  result_finish_date: string;

  assign_id?: string;
  assign_at?: string;
  assign_user?: IUser;

  status_id: number;
  status_at?: string;

  active: number;

  tasks?: Array<ITask>;
  comments?: Array<ITaskComment>;
  histories?: Array<ITaskHistory>;
  attachments?: Array<ITaskAttachment>;

  created_at: string;
  updated_at: string;
}

interface IPhase {
  id: string;
  title: string;
  description: string;
  index: number;
  project_id: string;
  plan_init_date: string;
  plan_finish_date: any;
  result_init_date: string;
  result_finish_date: string;
  tasks: Array<ITask>;
}

interface IProject {
  id: string;
  title: string;
  description: string;
  date_init: any;
  template_id: string;
  phases: Array<IPhase>;
  user_responsable_id?: string;
}

interface ITasksProp {
  tasks: Array<ITask>;
  onClick: (task: ITask) => void;
  onDuplicate: (task: ITask) => void;
}
const Tasks = ({ tasks, onClick, onDuplicate }: ITasksProp) => {
  return (
    <>
      {
        tasks.map((task: ITask) => (
          <>
        <Task
          key={`${task.parent_id}_${task.id}`}
          className="task"
          style={{
            borderColor: task.active && task.status_id === 1 && moment.utc(task.plan_init_date).isBefore(moment())
              ? '#faad14'
              : (
                task.active && task.status_id !== 2 && moment.utc(task.plan_finish_date).isBefore(moment())
                  ? '#ff4d4f'
                  : 'transparent'
              ),
            backgroundColor: !task.active ? '#dddddd' : '#ffffff',
            position: 'relative',
            cursor: 'pointer'
          }}
          onClick={() => onClick(task)}
        >
          <p className="title">
            {(task.attachments && task.attachments.length > 0) && (
              <Tooltip placement="top" title="Possui anexos">
                <LinkOutlined />{' '}
              </Tooltip>
            )}
            {task.title}
          </p>

          <div className="task_info">
            <Tooltip placement="top" title="Atruibuido à">
              <label><UserOutlined /> {task.assign_user?.name}</label>
            </Tooltip>
            <Tooltip placement="top" title="Status">
              <label>
                <FlagOutlined />{' ' + (!task.active ? 'Desativada' : statuses.find(st => st.id === task.status_id)?.title)}
              </label>
            </Tooltip>
          </div>

          {/* Botão duplicar */}
          <Tooltip title="Duplicar tarefa" placement="top">
            <Button
              size="small"
              type="primary"
              shape="circle"
              style={{ 
                position: 'absolute', 
                top: 5, 
                right: 5,
                width: '24px',
                height: '24px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.stopPropagation(); // evita abrir o modal
                onDuplicate(task);
              }}
            />
          </Tooltip>

          <div className="task_plan">
            <Tooltip placement="top" title="Data de início planejado">
              <label><CalendarOutlined /> {moment.utc(task.result_init_date).format('DD/MM/YYYY')}</label>
            </Tooltip>
            <Tooltip placement="top" title="Data de encerramento planejado">
              <label><CalendarOutlined /> {moment.utc(task.result_finish_date).format('DD/MM/YYYY')}</label>
            </Tooltip>
          </div>
        </Task>


            {
              !!task.tasks && <Tasks tasks={task.tasks} onClick={onClick} onDuplicate={onDuplicate} />
            }
          </>
        ))
      }
    </>
  );
}

interface ICommentProp {
  comment: ITaskComment;
  handleEdit: (id: string, text: string) => Promise<boolean>;
}

const Comment = ({ comment, handleEdit }: ICommentProp) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [text, setText] = useState<string>(comment.comment);
  const userLogged = useLogged();

  const handleSave = async () => {
    const success = await handleEdit(comment.id, text);
    setIsEditing(!success);
  }

  useEffect(() => {
    const newComment = comment.comment.replace(/\@\[(.*?)\]/g, "@$1").replace(/\([^)]+\)/g, "");
    setText(newComment);
  }, []);

  return (
    <div className="comment">
      <UserAvatar userName={comment.user.name} />
      <div className="comment_content">
        <p>
          <strong>{comment.user.name}</strong> {moment.utc(comment.updated_at).fromNow()}
          .
          {
            comment.user_id === userLogged.user.id && isEditing && <Button type="link" onClick={() => setIsEditing(false)}><small>Cancelar Edição</small></Button>
          }{
            comment.user_id === userLogged.user.id && !isEditing && <Button type="link" onClick={() => setIsEditing(true)}><small>Editar {comment.created_at !== comment.updated_at ? '(Editado)' : ''}</small></Button>
          }
        </p>
        {
          isEditing
            ? (
              <Input
                value={text}
                onChange={(event: any) => setText(event.target.value)}
                onPressEnter={handleSave}
                maxLength={250}
                autoFocus
              />
            )
            : (
              <div>
                {text}
              </div>
            )
        }
      </div>
    </div>
  )
}

interface IHistoryProp {
  history: ITaskHistory;
  users: IUser[];
}

const History = ({ history, users }: IHistoryProp) => {
  const keyMap = [
    { key: 'status_id', value: 'Status' },
    { key: 'assign_id', value: 'Atribuido à' },
    { key: 'active', value: 'Ativa' },
    { key: 'result_init_date', value: 'Data de início' },
    { key: 'result_finish_date', value: 'Data de encerramento' },
    { key: 'comment', value: 'comentário' },
  ];

  let initialMessage: string = 'Alterou o campo';
  let old_value: string = history.old_value;
  let new_value: string = history.new_value;
  let motive: string | undefined = history.motive;

  if (history.key === 'status_id') {
    old_value = statuses.find(st => st.id === Number(old_value))?.title || '...';
    new_value = statuses.find(st => st.id === Number(new_value))?.title || '...';
  }

  if (history.key === 'assign_id') {
    old_value = users.find(st => st.id === old_value)?.name || '...';
    new_value = users.find(st => st.id === new_value)?.name || '...';
  }

  if (history.key === 'active') {
    old_value = old_value === '1' ? 'Sim' : 'Não';
    new_value = new_value === '1' ? 'Sim' : 'Não';
    motive = motive ? motive : '';
  }

  if (history.key === 'result_init_date') {
    old_value = old_value ? moment.utc(old_value).format('DD/MM/YYYY') : '...';
    new_value = new_value ? moment.utc(new_value).format('DD/MM/YYYY') : '...';
  }

  if (history.key === 'result_finish_date') {
    old_value = old_value ? moment.utc(old_value).format('DD/MM/YYYY') : '...';
    new_value = new_value ? moment.utc(new_value).format('DD/MM/YYYY') : '...';
  }

  if (history.key === 'comment') {
    initialMessage = 'Alterou um';
    motive = motive ? motive : '';
  }

  return (
    <div className="history">
      <UserAvatar userName={history.user.name} />
      <div className="history_content">
        <p><strong>{history.user.name}</strong> {moment.utc(history.created_at).fromNow()}</p>
        <div>
          <p>{initialMessage} <strong>{keyMap.find(item => item.key === history.key)?.value}</strong> de <strong>{old_value}</strong> para <strong>{new_value}</strong></p>
          {
            !!motive && (
              <p>Motivo: <strong>{motive}</strong></p>
            )
          }
        </div>
      </div>
    </div>
  )
}
interface IAttachmentPreviewProp {
  url: string;
}

const AttachmentPreview = ({ url }: IAttachmentPreviewProp) => {
  const extImage = ['png', 'jpg', 'jpeg'];
  const extAttachment = url.split('.').pop();

  if (!extImage.includes(extAttachment?.toLowerCase() as string)) {
    return (
      <div className="attachment_preview_ext">
        {extAttachment}
      </div>
    );
  }

  return (
    <img src={url} className="attachment_preview_img" />
  )
}

interface IAttachmentProp {
  attachment: ITaskAttachment;
  onRemove: () => void
}

const Attachment = ({ attachment, onRemove }: IAttachmentProp) => {
  return (
    <div className="attachment">
      <AttachmentPreview url={attachment.url} />
      <div className="attachment_content">
        <p>
          <a href={attachment.url} target="_blank" rel="noreferrer">
            <strong>{attachment.filename}</strong>
          </a>
        </p>
        <p>
          Adicionado: {moment.utc(attachment.created_at).format('DD/MM/YYYY HH:mm:ss')} -
          <Popconfirm
            title="Deseja realmente excluir?"
            onConfirm={onRemove}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="link">Excluir</Button>
          </Popconfirm>
        </p>
      </div>
    </div>
  )
}

const initialNewTask: ITask = {
  title: '',
  description: '',
  days: 'working_days',
  duration: 1,
  phase_id: '',
  plan_init_date: '',
  status_id: 0,
  active: 1,
} as ITask;

export function ProjectDetails() {
  const [isLoading, setLoading] = useState(false);
  const [isModalLoading, setModalLoading] = useState(false);
  const [project, setProject] = useState<IProject>();
  const [users, setUsers] = useState<Array<IUser>>();

  const [canEditAssign, setCanEditAssign] = useState(false);
  const [canEditStatus, setCanEditStatus] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showTaskDisableModal, setShowTaskDisableModal] = useState(false);
  const [showAddTaskModal, setAddShowTaskModal] = useState(false);
  const [availableParents, setAvailableParents] = useState<ITask[]>([]);
  const [currentTask, setCurrentTask] = useState<ITask>({} as ITask);
  const [editTask, setEditTask] = useState<ITask | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [commentValue, setCommentValue] = useState<string>('');
  const [taskDisableMotive, setTaskDisableMotive] = useState<string>('');
  const [newTask, setNewTask] = useState<ITask>(initialNewTask);
  const [selectedFiles, setSelectedFiles] = useState<any>([]);

  const currentRoute = useParams();
  const { taskId } = useParams();
  const userLogged = useLogged();
  const roles = useRoles();

  const user = userLogged.user;
  const canEditProject = roles.permissions
    .find(permission => permission.name === 'editProject');

  const [formNewTask] = Form.useForm();

  const uploadRef = useRef<UploadFile | null>(null);

  const navigate = useNavigate();

  const fetchProject = async (id: string) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/projects/${id}`);
      setProject(data.data);
      console.log(data.data, 'projeto');
    } catch (error: any) {
      message.error('Erro ao carregar projeto');
    } finally {
      setLoading(false);
    }
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/users`);
      setUsers(data.data.users);
    } catch (error: any) {
      message.error('Erro ao buscar usuários');
    } finally {
      setLoading(false);
    }
  }

  const handleTaskClick = async (task: ITask) => {
    try {
      setCanEditAssign(!!canEditProject);
      setCommentValue('');
      setCanEditStatus(task.assign_id === user.id || !!canEditProject);

      const { data } = await api.get(`/projects/${project?.id}/task/${task.id}`);

      // Estado original da tarefa (não editável)
      setCurrentTask(data.data);
      // Estado local editável (cópia dos dados atuais)
      setEditTask({ ...data.data });
      setIsEditing(false);

      const comment_container = document.querySelector('.comment_container');
      comment_container?.scrollTo({ top: 0, behavior: 'smooth' });

      const histories_container = document.querySelector('.histories_container');
      histories_container?.scrollTo({ top: 0, behavior: 'smooth' });

      setShowTaskModal(true);
    } catch (error) {
      console.error(error);
    }
  };

  const findTaskById = (tasks: ITask[], taskId: string): ITask | null => {
    for (const task of tasks) {
      if (task.id === taskId) {
        return task;
      }

      if (task.tasks && task.tasks?.length > 0) {
        const nestedTask = findTaskById(task.tasks, taskId);
        if (nestedTask) {
          return nestedTask;
        }
      }
    }

    return null;
  };

  const handleSaveEditTask = async () => {
    if (!editTask || !currentTask || !project) return;
    
    // Descobrir campos alterados comparando com o estado original
    const camposAlterados: any = {};
    const campos: (keyof ITask)[] = [
      'assign_id',
      'parent_id',
      'status_id',
      'plan_finish_date',
      'result_init_date',
      'result_finish_date',
      'active',
    ];
    
    campos.forEach((campo) => {
      const valorOriginal = currentTask[campo];
      const valorEditado = editTask[campo];
      
      // Comparação mais robusta para datas
      if (campo.includes('_date')) {
        const dataOriginal = valorOriginal ? moment(valorOriginal).format('YYYY-MM-DD') : null;
        const dataEditada = valorEditado ? moment(valorEditado).format('YYYY-MM-DD') : null;
        
        if (dataOriginal !== dataEditada) {
          camposAlterados[campo] = valorEditado;
        }
      } else {
        if (valorOriginal !== valorEditado) {
          camposAlterados[campo] = valorEditado;
        }
      }
    });
    
    // Verifica se o responsável do projeto foi alterado
    // Nota: user_responsable_id é uma propriedade do projeto, não da tarefa
    // Esta verificação será feita no componente do responsável do projeto
    
    if (Object.keys(camposAlterados).length === 0) {
      setIsEditing(false);
      message.info('Nenhuma alteração detectada');
      return;
    }
    
    setModalLoading(true);
    try {
      console.log('Enviando alterações:', camposAlterados);
      
      const { data } = await api.post(`/projects/${project.id}/task-update/${currentTask.id}`, camposAlterados);
      
      if (data.data.task) {
        // Atualiza o estado original com os dados retornados
        setCurrentTask(data.data.task);
        // Atualiza o estado editável com os mesmos dados
        setEditTask({ ...data.data.task });
        setIsEditing(false);
        message.success('Tarefa atualizada com sucesso!');
        
        // Atualiza o projeto para refletir mudanças no kanban
        await fetchProject(project.id);
      }
    } catch (error: any) {
      console.error('Erro ao atualizar tarefa:', error);
      message.error(error.response?.data?.message || 'Erro ao atualizar tarefa');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCancelEdit = () => {
    // Restaura o estado editável para os valores originais
    setEditTask({ ...currentTask });
    setIsEditing(false);
    message.info('Alterações canceladas');
  };

  const handleDuplicateTask = () => {
    if (!currentTask || !project) return;
    
    // Cria uma cópia da tarefa atual para edição
    const duplicatedTask = {
      ...currentTask,
      id: '', // Remove o ID para criar uma nova tarefa
      title: `${currentTask.title}`,
      status_id: 0, // Define como "Não iniciada"
      // Mantém assign_id, result_init_date e result_finish_date da tarefa original
      comments: [], // Remove comentários
      histories: [], // Remove histórico
      attachments: [], // Remove anexos
    };
    
    setEditTask(duplicatedTask);
    setIsDuplicating(true);
    setIsEditing(true);
  };

  const handleQuickDuplicate = async (task: ITask) => {
    if (!project) return;
    
    try {
      setLoading(true);
      // Usa a rota de duplicação específica
      const { data } = await api.post(`/projects/${project.id}/duplicate-task/${task.id}`, {
        project_id: project.id,
        title: `${task.title}`,
        description: task.description,
        duration: task.duration,
        days: task.days,
        phase_id: task.phase_id,
        parent_id: task.parent_id,
        plan_init_date: task.plan_init_date,
        assign_id: task.assign_id,
        status_id: 0, // Define como "Não iniciada"
        active: task.active,
        result_init_date: task.result_init_date,
        result_finish_date: task.result_finish_date,
      });
      
      if (data.data) {
        message.success('Tarefa duplicada com sucesso!');
        // Atualiza o projeto para mostrar a nova tarefa
        await fetchProject(project.id);
      }
    } catch (error: any) {
      console.error('Erro ao duplicar tarefa:', error);
      message.error(error.response?.data?.message || 'Erro ao duplicar tarefa');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDuplicate = async () => {
    if (!editTask || !project || !currentTask) return;
    
    setModalLoading(true);
    try {
      // Usa a rota de duplicação específica
      const { data } = await api.post(`/projects/${project.id}/duplicate-task/${currentTask.id}`, {
        project_id: project.id,
        title: editTask.title,
        description: editTask.description,
        duration: editTask.duration,
        days: editTask.days,
        phase_id: editTask.phase_id,
        parent_id: editTask.parent_id,
        plan_init_date: editTask.plan_init_date,
        assign_id: editTask.assign_id,
        status_id: editTask.status_id,
        active: editTask.active,
        result_init_date: editTask.result_init_date,
        result_finish_date: editTask.result_finish_date,
      });
      
      if (data.data) {
        message.success('Tarefa duplicada com sucesso!');
        setIsDuplicating(false);
        setIsEditing(false);
        setShowTaskModal(false);
        // Atualiza o projeto para mostrar a nova tarefa
        await fetchProject(project.id);
      }
    } catch (error: any) {
      console.error('Erro ao duplicar tarefa:', error);
      message.error(error.response?.data?.message || 'Erro ao duplicar tarefa');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCancelDuplicate = () => {
    setIsDuplicating(false);
    setIsEditing(false);
    setEditTask({ ...currentTask });
    message.info('Duplicação cancelada');
  };

  useEffect(() => {
    if (taskId !== undefined && project) {
      const openTask = project.phases
        .map((phase) => findTaskById(phase.tasks, taskId))
        .filter((task) => task !== null)
        .pop();

      if (openTask) {
        handleTaskClick(openTask);
      }
    }
  }, [taskId, project]);



  const handleAddComment = async (event: any) => {
    try {
      setModalLoading(true);
      const body = { user_id: userLogged.user.id, comment: commentValue };
      const { data } = await api.post(`/projects/${project?.id}/task-comment/${currentTask.id}`, body);
      setCurrentTask({
        ...currentTask,
        comments: data.data.comments
      });
      setCommentValue('')
    } catch (error: any) {
      message.error('Erro ao comentar');
    } finally {
      setModalLoading(false);
    }
  }

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddComment(e);
    }
  };

  const handleEditComment = async (id: string, text: any) => {
    try {
      setModalLoading(true);
      const { data } = await api.post(`/projects/${project?.id}/task-comment/${currentTask.id}/edit/${id}`, {
        comment: text
      });
      setCurrentTask({
        ...currentTask,
        comments: data.data.comments,
        histories: data.data.histories,
      });
      return true;
    } catch (error: any) {
      message.error('Erro editar comentário');
      return false;
    } finally {
      setModalLoading(false);
    }
  }

  const handleShowTaskDisableModal = () => {
    setShowTaskDisableModal(true);
    setTaskDisableMotive('');
  }

  const handleDisableTask = async () => {
    if (!canEditAssign) {
      message.warn('Você não pode desativar essa tarefa.');
      return;
    }

    if (currentTask.status_id == 2) {
      message.warn('Você não pode desativar uma tarefa concluida tarefa.');
      return;
    }

    try {
      setModalLoading(true);
      const body = { motive: taskDisableMotive };
      const { data } = await api.post(`/projects/${project?.id}/task-disable/${currentTask.id}`, body);
      setCurrentTask({
        ...currentTask,
        active: data.data.active,
        histories: data.data.histories
      });
      setShowTaskDisableModal(false);
    } catch (error) {
      message.error('Erro ao desativar tarefa');
    } finally {
      setModalLoading(false);
    }
  }


  const handleProjectResponsable = async (userId: string) => {
    try {
      setLoading(true);
      const body = { user_responsable_id: userId };
      const { data } = await api.post(`/projects/${project?.id}/task-update/${currentTask.id}`, body);
      setProject({
        ...project,
        user_responsable_id: userId
      } as IProject)
    } catch (error: any) {
      message.error('Erro ao definir responsável');
    } finally {
      setLoading(false);
    }
  }

  const handleOpenAddTask = async (project: IProject, phase_id: string) => {
    const phase = project.phases.find(phase => phase.id === phase_id);
    const currentPhaseIndex = phase?.index ?? 0;
    const availablePhases = project.phases.filter(p => p.index <= currentPhaseIndex);
    let allTasks: ITask[] = [];
    availablePhases.forEach(p => {
      allTasks = [...allTasks, ...collectTasks(p.tasks ?? [])];
    });
    setAddShowTaskModal(true);
    setAvailableParents(allTasks);
    setNewTask({
      title: initialNewTask.title,
      description: initialNewTask.description,
      days: initialNewTask.days,
      duration: initialNewTask.duration,
      status_id: initialNewTask.status_id,
      active: initialNewTask.active,
      phase_id,
    } as ITask);
    formNewTask.resetFields();
  }

  function collectTasks(tasks: ITask[], result: ITask[] = []) {
    tasks.forEach(task => {
      result.push(task);
      if (task.tasks && task.tasks.length > 0) {
        result = [...result, ...collectTasks(task.tasks, result)];
      }
    });

    return result.reduce((acc, task) => {
      if (!acc.some(item => item.id === task.id)) {
        acc.push(task);
      }
      return acc;
    }, [] as ITask[]);
  }

  const handleAddTask = async (form: any) => {
    try {
      setModalLoading(true);
      const body = {
        ...newTask,
        ...form
      };
      await api.post(`/projects/${project?.id}/add-task`, body)
      .then(
        async () => {await fetchProject(project?.id as string);}
      )
      .then(() => {
        setAddShowTaskModal(false);
      });
    } catch (error: any) {
      message.error('Erro ao adicionar tarefa');
    } finally {
      setModalLoading(false);
    }
  }

  const handleUploadAttachment = async (event: any) => {
    try {
      setModalLoading(true);
      const formData = new FormData();

      event.fileList.forEach((file: any) => {
        formData.append("files", file.originFileObj);
        formData.append("filenames", file.name);
      });

      const { data } = await api.post(`/projects/${project?.id}/task-attachment/${currentTask.id}`, formData);
      setCurrentTask({
        ...currentTask,
        attachments: data.data.attachments
      });
    } catch (error: any) {
      message.error('Erro ao adicionar anexos');
    } finally {
      setModalLoading(false);
    }
  }

  const handleFileSelection = (event: any) => {
    const filesToAdd = event.fileList;

    const newFiles = Array.from(filesToAdd).filter((file: any) => {
      return !selectedFiles.some((selectedFile: any) => selectedFile.name === file.name);
    });

    setSelectedFiles([...selectedFiles, ...newFiles]);
  };

  const handleSaveFiles = async () => {
    setModalLoading(true);
    const formData = new FormData();


    selectedFiles.forEach((file: any) => {
      formData.append("files", file.originFileObj);
    });

    try {
      const { data } = await api.post(`/projects/${project?.id}/task-attachment/${currentTask.id}`, formData);
      setCurrentTask({
        ...currentTask,
        attachments: data.data[0].attachments
      });

      setSelectedFiles([]);
    } catch (error) {
      message.error('Erro ao adicionar anexos');
    } finally {
      setModalLoading(false);
    }
  };

  const handleRemoveAttachment = async (attachment_id: string) => {
    try {
      setModalLoading(true);
      await api.delete(`/projects/${project?.id}/task-attachment/${attachment_id}`);
      setCurrentTask({
        ...currentTask,
        attachments: currentTask.attachments?.filter(attachment => attachment.id !== attachment_id),
      })
    } catch (error: any) {
      message.error('Erro ao remover anexo');
    } finally {
      setModalLoading(false);
    }
  }

  useEffect(() => {
    fetchProject(currentRoute.id as string);
    fetchUsers();
  }, []);

  let mentionData: any = [];

  mentionData = users?.map((userData) => ({
    id: userData.id,
    display: userData.name,
  }));

  return (
    <>
      <ContentHeader>
        <h3>{project?.title}</h3>
        <Select
          showSearch
          placeholder="Responsável pelo projeto"
          optionFilterProp="children"
          filterOption={(input: any, option: any) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          filterSort={(optionA: any, optionB: any) =>
            optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
          }
          value={project?.user_responsable_id}
          onChange={handleProjectResponsable}
          disabled={isLoading || !canEditProject}
          style={{ width: '300px' }}
        >
          {
            users?.map(user => (
              <Select.Option value={user.id}>{user.name}</Select.Option>
            ))
          }
        </Select>
      </ContentHeader>

      <Kanban>
        {
          !isLoading && project && project.phases.sort((a, b) => a.index - b.index).map(phase => (
            <Phase>
              <div className="title-container">
                <p className="title">{phase.title}</p>
                <Tooltip title="Adicionar tarefa">
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleOpenAddTask(project, phase.id)}
                  />
                </Tooltip>
              </div>

              <TasksContainer>
                <Tasks tasks={phase.tasks} onClick={handleTaskClick} onDuplicate={handleQuickDuplicate} />
              </TasksContainer>
            </Phase>
          ))
        }
        {
          isLoading && (
            <Loading>
              <LoadingOutlined style={{ fontSize: '2rem' }} />
            </Loading>
          )
        }
      </Kanban>

      <Modal
        title={isDuplicating ? `Duplicar: ${currentTask.title}` : currentTask.title}
        visible={showTaskModal}
        footer={null}
        width='60%'
        style={{ top: 20 }}
        centered={false}
        bodyStyle={{
          padding: '10px'
        }}
        onCancel={() => {
          if (!isModalLoading) {
            setShowTaskModal(false);
            setIsDuplicating(false);
            fetchProject(currentRoute.id as string);
            navigate(`/project-details/${currentRoute.id}`);
          }
        }}
      >
        <ModalTaskBody>
          {isDuplicating && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label>Título da tarefa:</label>
                  <Input
                    value={editTask?.title || ''}
                    onChange={(e) => {
                      if (!editTask) return;
                      setEditTask({ ...editTask, title: e.target.value });
                    }}
                    placeholder="Título da tarefa"
                    maxLength={50}
                  />
                </div>
                <div style={{ width: '120px' }}>
                  <label>Duração:</label>
                  <Input
                    type="number"
                    min={1}
                    value={editTask?.duration || 1}
                    onChange={(e) => {
                      if (!editTask) return;
                      setEditTask({ ...editTask, duration: parseInt(e.target.value) || 1 });
                    }}
                    placeholder="Dias"
                  />
                </div>
                <div style={{ width: '150px' }}>
                  <label>Tipo de dias:</label>
                  <Select
                    value={editTask?.days || 'working_days'}
                    onChange={(value) => {
                      if (!editTask) return;
                      setEditTask({ ...editTask, days: value });
                    }}
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="working_days">Dias úteis</Select.Option>
                    <Select.Option value="calendar_days">Dias corridos</Select.Option>
                  </Select>
                </div>
              </div>
            </div>
          )}
          
          <div className="task_description">
            {isDuplicating ? (
              <Input.TextArea
                value={editTask?.description || ''}
                onChange={(e) => {
                  if (!editTask) return;
                  setEditTask({ ...editTask, description: e.target.value });
                }}
                rows={3}
                placeholder="Descrição da tarefa"
                maxLength={255}
              />
            ) : (
              currentTask.description
            )}
          </div>

          {!isDuplicating && canEditProject && (
            <div style={{ marginBottom: '16px' }}>
              <Button
                type="default"
                icon={<PlusOutlined />}
                onClick={handleDuplicateTask}
                disabled={isModalLoading}
              >
                Duplicar Tarefa
              </Button>
            </div>
          )}

          <DisplayInRow>
            <DisplayInColumn style={{ width: '45%' }}>
              <div className="task_plan">
                <Card title="Previsão">
                  <Col>
                    <label><CalendarOutlined /> Data de início:</label>
                    <br />
                    <DatePicker
                      value={editTask?.plan_init_date ? moment.utc(editTask.plan_init_date) : null}
                      format='DD/MM/YYYY'
                      onChange={(event: any) => {
                        if (!editTask) return;
                        setEditTask({ ...editTask, plan_init_date: event ? event.toISOString() : '' });
                        if (!isDuplicating) setIsEditing(true);
                      }}
                      disabled={!isDuplicating && !canEditProject}
                    />
                  </Col>
                  <Col>
                    <label><CalendarOutlined /> Data de encerramento:</label>
                    <br />
                    <DatePicker
                      value={editTask?.plan_finish_date ? moment.utc(editTask.plan_finish_date) : null}
                      format='DD/MM/YYYY'
                      onChange={(event: any) => {
                        if (!editTask) return;
                        setEditTask({ ...editTask, plan_finish_date: event ? event.toISOString() : '' });
                        if (!isDuplicating) setIsEditing(true);
                      }}
                      disabled={!isDuplicating && (!canEditProject || isModalLoading || (!!editTask && editTask.status_id === 2))}
                    />
                  </Col>
                </Card>
              </div>

              <VerticalSpace />

              <div className="task_executed">
                <Card title="Realizado">
                  <Col>
                    <label><CalendarOutlined /> Data de início:</label>
                    <br />
                    <DatePicker
                      value={editTask?.result_init_date ? moment.utc(editTask.result_init_date) : null}
                      format='DD/MM/YYYY'
                      onChange={(event: any) => {
                        if (!editTask) return;
                        setEditTask({ ...editTask, result_init_date: event ? event.toISOString() : '' });
                        setIsEditing(true);
                      }}
                      disabled={!canEditProject ? true : isModalLoading}
                    />
                  </Col>
                  <Col>
                    <label><CalendarOutlined /> Data de encerramento:</label>
                    <br />
                    <DatePicker
                      value={editTask?.result_finish_date ? moment.utc(editTask.result_finish_date) : null}
                      format='DD/MM/YYYY'
                      onChange={(event: any) => {
                        if (!editTask) return;
                        setEditTask({ ...editTask, result_finish_date: event ? event.toISOString() : '' });
                        setIsEditing(true);
                      }}
                      disabled={!canEditProject ? true : isModalLoading}
                    />
                  </Col>
                </Card>
              </div>
            </DisplayInColumn>

            <HorizontalSpace />

            <DisplayInColumn style={{ flex: 1, minHeight: '210px', justifyContent: 'flex-start' }}>
              <div className="task_parent">
                <label>Dependência:</label>
                {(isDuplicating || (canEditStatus && editTask && editTask.status_id !== 2))
                    ?
                   <Select
                    placeholder="Tarefa anterior"
                    style={{ width: "100%" }}
                    value={editTask?.parent_id}
                    onChange={(value) => {
                      if (!editTask) return;
                      setEditTask({ ...editTask, parent_id: value });
                      if (!isDuplicating) setIsEditing(true);
                    }}
                    allowClear
                  >
                    {project?.phases.flatMap(phase => phase.tasks).filter(task => task.id !== editTask?.id).map(task => (
                      <Select.Option key={task.id} value={task.id}>{task.title}</Select.Option>
                    ))}
                  </Select>
                  :
                  <Input
                        disabled
                        value={project?.phases.flatMap(phase => phase.tasks).find(task => task.id === editTask?.parent_id)?.title || 'Nenhuma dependência definida'}
                      />}
              </div>
              <div className="task_status">
                <label>Status:</label>
                {
                  (isDuplicating || (canEditStatus && editTask && editTask.assign_id && editTask.status_id !== 2))
                    ? (
                      <Select
                        placeholder="Selecione um status"
                        value={editTask?.status_id}
                        onChange={(value) => {
                          if (!editTask) return;
                          setEditTask({ ...editTask, status_id: value });
                          if (!isDuplicating) setIsEditing(true);
                        }}
                        disabled={isModalLoading}
                      >
                        {
                          statuses?.map(status => (
                            <Select.Option
                              key={status.id}
                              value={status.id}
                            >
                              {status.title}
                            </Select.Option>
                          ))
                        }
                      </Select>
                    )
                    : (
                      <Input
                        disabled
                        value={statuses.find(st => st.id === editTask?.status_id)?.title}
                      />
                    )
                }
              </div>
              <div className="task_assigned_user">
                <label>Atribuído à:</label>
                {
                  (isDuplicating || (canEditAssign && editTask && editTask.status_id !== 2))
                    ? (
                      <Select
                        showSearch
                        placeholder="Selecione um usuário"
                        optionFilterProp="children"
                        filterOption={(input: any, option: any) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA: any, optionB: any) =>
                          optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                        }
                        value={editTask?.assign_id}
                        onChange={(value) => {
                          if (!editTask) return;
                          setEditTask({ ...editTask, assign_id: value });
                          if (!isDuplicating) setIsEditing(true);
                        }}
                        disabled={isModalLoading}
                        allowClear
                      >
                        {
                          users?.map(user => (
                            <Select.Option key={user.id} value={user.id}>{user.name}</Select.Option>
                          ))
                        }
                      </Select>
                    )
                    : (
                      <Input
                        disabled
                        value={editTask?.assign_user?.name}
                      />
                    )
                }
              </div>
            </DisplayInColumn>
          </DisplayInRow>

          {isEditing && (
            <div style={{ display: 'flex', gap: 8, margin: '16px 0', alignItems: 'center' }}>
              {isDuplicating ? (
                <>
                  <Button type="primary" loading={isModalLoading} onClick={handleSaveDuplicate}>
                    Duplicar Tarefa
                  </Button>
                  <Button onClick={handleCancelDuplicate} disabled={isModalLoading}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <>
                  <Button type="primary" loading={isModalLoading} onClick={handleSaveEditTask}>
                    Salvar
                  </Button>
                  <Button onClick={handleCancelEdit} disabled={isModalLoading}>
                    Cancelar
                  </Button>
                  {canEditAssign && editTask && editTask.status_id !== 2 && (
                    <Button
                      onClick={handleShowTaskDisableModal}
                      disabled={isModalLoading || editTask.active === 0}
                      danger
                    >
                      Desativar tarefa
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {!isDuplicating && (
            <Tabs defaultActiveKey="1">
              <Tabs.TabPane tab="Comentários" key="comments">
              <div className="task_comments">
                {
                  canEditStatus && (
                    <div className="add_comment">
                      <UserAvatar />
                      <MentionsInput
                        value={commentValue}
                        onChange={(e) => setCommentValue(e.target.value)}
                        maxLength={250}
                        style={{ width: '100%' }}
                        onKeyDown={handleKeyPress}
                      >
                        <Mention
                          trigger="@"
                          data={mentionData}
                          displayTransform={(id, display) => `@${display}`}
                        />
                      </MentionsInput>
                      {/* <Input
                        value={commentValue}
                        onChange={(event: any) => setCommentValue(event.target.value)}
                        onPressEnter={handleAddComment}
                        maxLength={250}
                      /> */}
                    </div>
                  )
                }

                <div className="comment_container">
                  {
                    !!currentTask.comments && currentTask.comments.map(comment => (
                      <Comment
                        comment={comment}
                        handleEdit={handleEditComment}
                      />
                    ))
                  }
                </div>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Anexos" key="attachments">
              <div className="task_attachments">
                {
                  (canEditStatus || userLogged.user.id === currentTask.assign_id) && (
                    <div className="add_attachment">
                      {selectedFiles.length === 0 && (
                        <Upload
                          ref={uploadRef}
                          onChange={handleFileSelection}
                          beforeUpload={() => false}
                          showUploadList={false}
                          fileList={[]}
                          multiple={true}
                        >
                          <Button
                            loading={isModalLoading}
                            icon={<UploadOutlined />}
                          >
                            Adicionar
                          </Button>
                        </Upload>
                      )}
                      {selectedFiles.length > 0 && (
                        <div>
                          <h3>Arquivos Selecionados:</h3>
                          <ul>
                            {selectedFiles.map((file: any, index: number) => (
                              <li key={index}>{file.name}</li>
                            ))}
                          </ul>
                          <Button
                            onClick={handleSaveFiles}
                            disabled={selectedFiles.length === 0 || isModalLoading}
                            icon={<SaveOutlined />}
                          >
                            Salvar arquivos
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                }

                <div className="attachment_container">
                  {
                    !!currentTask.attachments && currentTask.attachments.map(attachment => (
                      <Attachment
                        attachment={attachment}
                        onRemove={() => handleRemoveAttachment(attachment.id)}
                      />
                    ))
                  }
                </div>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Alterações" key="histories">
              <div className="histories_container">
                {
                  !!currentTask.histories && currentTask.histories.sort((a, b) => a.created_at >= b.created_at ? -1 : 1).map(history => (
                    <History history={history} users={users || []} />
                  ))
                }
              </div>
            </Tabs.TabPane>
          </Tabs>
          )}
        </ModalTaskBody>
      </Modal>

      <Modal
        title={`Desativar tarefa: ${currentTask.title}`}
        visible={showTaskDisableModal}
        onOk={handleDisableTask}
        confirmLoading={isModalLoading}
        onCancel={() => setShowTaskDisableModal(false)}
      >
        <p>Essa ação não podera ser desfeita!</p>
        <p>Tem certeza que deseja desativar essa tarefa?</p>
        <Input
          placeholder="Motivo da desativação (optional)"
          maxLength={255}
          value={taskDisableMotive}
          onChange={(event) => setTaskDisableMotive(event.target.value)}
        />
      </Modal>

      <Modal
        title="Adicionar tarefa"
        visible={showAddTaskModal}
        confirmLoading={isModalLoading}
        onOk={() => formNewTask.submit()}
        onCancel={() => setAddShowTaskModal(false)}
      >
        <Form
          form={formNewTask}
          initialValues={newTask}
          onFinish={handleAddTask}
        >
          <Form.Item
            name="title"
            label="Nome"
            style={{
              marginBottom: "25px",
            }}
            rules={[
              {
                required: true,
                message: "Este campo é obrigatório!",
              },
            ]}
          >
            <Input
              maxLength={50}
              placeholder="Nome da tarefa"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descrição"
            style={{
              marginBottom: "25px",
            }}
            rules={[
              {
                required: true,
                message: "Este campo é obrigatório!",
              },
            ]}
          >
            <Input
              maxLength={50}
              placeholder="Descrição da tarefa"
            />
          </Form.Item>

          <Form.Item
            name="duration"
            label="Duração da tarefa"
            style={{
              marginBottom: "25px",
            }}
            rules={[
              {
                required: true,
                message: "Este campo é obrigatório!",
              },
            ]}
          >
            <Input
              type="number"
              min={1}
              step={1}
              placeholder="Duração da tarefa"
            />
          </Form.Item>

          <Form.Item
            name="days"
            label="Dias contáveis"
            style={{
              marginBottom: "25px",
            }}
            rules={[
              {
                required: true,
                message: "Este campo é obrigatório!",
              },
            ]}
          >
            <Select
              placeholder="Selecione os dias contáveis"
              style={{ width: "100%" }}
            >
              <Select.Option value="working_days">Dias úteis</Select.Option>
              <Select.Option value="calendar_days">Dias corridos</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="parent_id"
            label="Dependência"
            style={{
              marginBottom: "25px",
            }}
          >
            <Select
              placeholder="Tarefa anterior"
              style={{ width: "100%" }}
              allowClear
            >
              <Select.Option value={null}>Sem dependência</Select.Option>
              {
                availableParents.map((availableParent) => (
                  <Select.Option value={availableParent.id}>{availableParent.title}</Select.Option>
                ))
              }
            </Select>
          </Form.Item>

          <Form.Item
            shouldUpdate={(prevValues, curValues) => prevValues.parent_id !== curValues.parent_id}
          >
            {({ getFieldValue }) => (
              <Form.Item
                name="plan_init_date"
                label="Data de início"
                style={{ marginBottom: "25px" }}
                rules={[{
                  required: !getFieldValue('parent_id'),
                  message: "Este campo é obrigatório!",
                }]}
              >
                <DatePicker
                  placeholder="Selecione uma data"
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  disabled={!!getFieldValue('parent_id')}
                />
              </Form.Item>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
